import React, {useState} from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Grid,
    Paper,
    styled,
    Typography
} from '@mui/material';
import {apiAisStatus} from "@/modules/trouble";
import {useTheme} from "@mui/material/styles";
import {ArrowDownward, ArrowUpward, NetworkPing} from "@mui/icons-material";
import PageHeader from "@/global/components/PageHeader";
import {PingTest} from "@/modules/trouble/pages/site/components";
import {useSpeedTest} from "@/modules/trouble/pages/site/hooks/useSpeedTest";

const SpeedGauge = styled(Box)(({theme}) => ({
    position: "relative",
    width: "280px",
    height: "280px",
    borderRadius: "50%",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.background.paper,
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}));

const SpeedValue = styled(Box)(() => ({
    textAlign: "center",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
}));

const ResultCard = styled(Card)(() => ({
    textAlign: "center",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
        boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
        transform: "translateY(-2px)",
    },
}));

const StartButton = styled(Button)(() => ({
    borderRadius: "30px",
    padding: "10px 30px",
    fontWeight: "bold",
    textTransform: "none",
    fontSize: "16px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
        boxShadow: "0 6px 15px rgba(0,0,0,0.15)",
        transform: "translateY(-2px)",
    },
}));

export function TroubleSitePage() {
    const [additionalInfo, setAdditionalInfo] = useState<boolean>(false);

    const theme = useTheme();
    const {data} = apiAisStatus();
    const {currentPhase, totalPing, downloadSpeed, uploadSpeed, progress, pingResults, isTestRunning, startSpeedTest} = useSpeedTest();

    const getPhaseLabel = (): string => {
        switch (currentPhase) {
            case "ping":
                return "Тестирование пинга";
            case "download":
                return "Тестирование скачивания";
            case "upload":
                return "Тестирование загрузки";
            case "complete":
                return "Тесты завершены";
            default:
                return "Готово к тестированию";
        }
    };

    const getSpeedColor = (speed: number): string => {
        if (speed > 80) return "#4CAF50";
        if (speed > 30) return "#FFC107";
        return "#F44336";
    };

    const getDisplayValue = (): { value: string | number; unit: string } => {
        switch (currentPhase) {
            case "ping":
                return {value: (totalPing.pingTime / totalPing.iteration).toFixed(2), unit: "ms"};
            case "download":
                return {value: downloadSpeed.toFixed(2), unit: "Mbps"};
            case "upload":
                return {value: uploadSpeed.toFixed(2), unit: "Mbps"};
            case "complete":
                return {value: downloadSpeed.toFixed(2), unit: "Mbps"};
            default:
                return {value: "--", unit: ""};
        }
    };

    const displayValue = getDisplayValue();
    const speedColor =
        currentPhase === "download" || currentPhase === "upload" || currentPhase === "complete"
            ? getSpeedColor(Number(displayValue.value))
            : theme.palette.primary.main;

    return (
        <Box
            className="App"
            sx={{height: '100%', display: 'flex', flexDirection: 'column'}}
        >
            <PageHeader/>
            <Container maxWidth="md">
                <Box sx={{py: 4}}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            background: "linear-gradient(to bottom, #f8f9fa, #ffffff)",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                        }}
                    >
                        <Box sx={{display: "flex", justifyContent: "space-between", mb: 4, alignItems: "center"}}>
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                color="primary"
                            >
                                Замеры скорости на серверах
                            </Typography>
                        </Box>

                        <Typography
                            variant={'h6'}
                            sx={{
                                mb: 4
                            }}
                        >
                            Статус Аис автошколы:
                            {' '}
                            {
                                data ?
                                    data.status ? (
                                        <Chip
                                            color={'success'}
                                            label={'Работает'}
                                        />
                                    ) : (
                                        <Chip
                                            color={'error'}
                                            label={'Не работает'}
                                        />
                                    )
                                    :
                                    <CircularProgress/>
                            }
                        </Typography>

                        <Box sx={{textAlign: "center", mb: 4}}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    mb: 2,
                                    fontWeight: "medium",
                                    color: "text.secondary",
                                    letterSpacing: 1,
                                }}
                            >
                                {getPhaseLabel()}
                            </Typography>

                            <SpeedGauge>
                                <Box sx={{position: "relative", width: "100%", height: "100%"}}>
                                    <CircularProgress
                                        variant="determinate"
                                        value={100}
                                        size={280}
                                        thickness={4}
                                        sx={{
                                            color: theme.palette.grey[200],
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                        }}
                                    />
                                    <CircularProgress
                                        variant="determinate"
                                        value={progress}
                                        size={280}
                                        thickness={4}
                                        sx={{
                                            color: speedColor,
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            "& .MuiCircularProgress-circle": {
                                                strokeLinecap: "round",
                                            },
                                        }}
                                    />
                                    <SpeedValue>
                                        <Typography
                                            variant="h2"
                                            component="div"
                                            fontWeight="bold"
                                            sx={{color: speedColor}}
                                        >
                                            {displayValue.value}
                                        </Typography>
                                        <Typography
                                            variant="subtitle1"
                                            color="text.secondary"
                                        >
                                            {displayValue.unit}
                                        </Typography>
                                    </SpeedValue>
                                </Box>
                            </SpeedGauge>

                            <Box sx={{mt: 3}}>
                                <StartButton
                                    variant="contained"
                                    color={isTestRunning ? "error" : "primary"}
                                    onClick={startSpeedTest}
                                    disabled={isTestRunning}
                                    disableElevation
                                >
                                    {isTestRunning ? "Тестирование..." : "Начать тестирование"}
                                </StartButton>
                            </Box>
                        </Box>

                        <Divider sx={{my: 4}}/>

                        <Grid
                            container
                            spacing={3}
                        >
                            <Grid
                                item
                                xs={12}
                                sm={4}
                            >
                                <ResultCard
                                    sx={{
                                        cursor: "pointer",
                                    }}
                                    onClick={() => setAdditionalInfo((prev) => !prev)}
                                >
                                    <CardContent>
                                        <NetworkPing sx={{fontSize: 40, color: theme.palette.grey[500], mb: 1}}/>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                        >
                                            Пинг
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                            color="text.primary"
                                        >
                                            {totalPing.pingTime !== 0 ? (totalPing.pingTime / totalPing.iteration).toFixed(2) : 0}
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                ms
                                            </Typography>
                                        </Typography>
                                    </CardContent>
                                </ResultCard>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={4}
                            >
                                <ResultCard>
                                    <CardContent>
                                        <ArrowDownward sx={{fontSize: 40, color: theme.palette.grey[500], mb: 1}}/>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                        >
                                            Скачивание
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                            color="text.primary"
                                        >
                                            {downloadSpeed.toFixed(2)}
                                            {" "}
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Mbps
                                            </Typography>
                                        </Typography>
                                    </CardContent>
                                </ResultCard>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={4}
                            >
                                <ResultCard>
                                    <CardContent>
                                        <ArrowUpward sx={{fontSize: 40, color: theme.palette.grey[500], mb: 1}}/>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                        >
                                            Загрузка
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                            color="text.primary"
                                        >
                                            {uploadSpeed.toFixed(2)}
                                            {" "}
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Mbps
                                            </Typography>
                                        </Typography>
                                    </CardContent>
                                </ResultCard>
                            </Grid>
                        </Grid>
                        <Box
                            sx={{
                                maxHeight: additionalInfo ? '800px' : '0px',
                                overflow: 'hidden',
                                transition: 'max-height 0.6s ease',
                            }}
                        >
                            <PingTest pingResults={pingResults}/>
                        </Box>
                        <Box sx={{mt: 4, textAlign: "center"}}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Сервера:
                                {' '}
                                {Object.keys(pingResults).reduce((el, currentValue, currentIndex) => {
                                    if (currentIndex === 0) {
                                        return el + `${currentValue}`;
                                    }
                                    return el + `, ${currentValue}`;
                                }, '')}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
}